// Figure image slot (S3): the figure rendered from its page region by the API, fetched with the session cookie only once it nears the viewport, shown from a blob URL that is revoked on change and unmount, on white paper in both themes, with a skeleton while loading and a compact problem with retry on failure.
import { useEffect, useState } from "react";

import { useBlobUrl } from "@/components/common/BlobImage";
import { ProblemState } from "@/components/common/ProblemState";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/cn";
import { encodeUid } from "@/lib/url";

export interface FigureImageProps {
  docUid: string;
  figureKey: string;
  alt: string;
  dpi?: number;
  className?: string;
}

const NEAR_VIEWPORT = "240px";

export function figureImagePath(docUid: string, figureKey: string, dpi?: number): string {
  const query = dpi === undefined ? "" : `?dpi=${String(dpi)}`;
  return `/api/v1/documents/${encodeUid(docUid)}/figures/${encodeUid(figureKey)}/image${query}`;
}

function useNearViewport(element: Element | null): boolean {
  const [near, setNear] = useState(() => typeof IntersectionObserver === "undefined");
  useEffect(() => {
    if (element === null || near || typeof IntersectionObserver === "undefined") {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNear(true);
          observer.disconnect();
        }
      },
      { rootMargin: NEAR_VIEWPORT },
    );
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [element, near]);
  return near;
}

export function FigureImage({ docUid, figureKey, alt, dpi, className }: FigureImageProps) {
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const near = useNearViewport(host);
  const { url, isLoading, error, refetch } = useBlobUrl(
    near ? figureImagePath(docUid, figureKey, dpi) : null,
  );
  let body;
  if (error) {
    body = <ProblemState problem={error} retry={refetch} compact className="m-2 w-[calc(100%-1rem)]" />;
  } else if (isLoading || url === null) {
    body = (
      <>
        <Skeleton className="aspect-[4/3] w-full rounded-none bg-[#eceff3]" />
        <span className="sr-only">{alt}</span>
      </>
    );
  } else {
    body = <img src={url} alt={alt} className="block h-auto w-full" />;
  }
  return (
    <div
      ref={setHost}
      aria-busy={error ? undefined : url === null}
      className={cn("relative overflow-hidden rounded-sm border bg-paper", className)}
    >
      {body}
    </div>
  );
}
